import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { ChevronLeft, ChevronRight, Play, X } from 'lucide-react';

const VIDEOS = [
  { id: 'dr0mDkV3gtc', author: 'Stéphan · promotion 2024' },
  { id: 'Fnt3KQDdLGQ', author: 'Virginie · promotion 2024' },
  { id: 'VkpYj_w7KXI', author: 'Promotion 2023' },
  { id: 'LgyXuQWqVFw', author: 'Régis · promotion 2024' },
  { id: 'U0KosMs8rN4', author: 'Formation accélérée · 2023' },
];

const ROTATION_MS = 6000;
const VISIBLE = 3; // tiles visible at once (desktop)

const embedUrl = (id, { muted = true, autoplay = true, controls = false, loop = true } = {}) => {
  const params = new URLSearchParams({
    autoplay: autoplay ? '1' : '0',
    mute: muted ? '1' : '0',
    controls: controls ? '1' : '0',
    modestbranding: '1',
    rel: '0',
    playsinline: '1',
    iv_load_policy: '3',
    fs: controls ? '1' : '0',
  });
  if (loop) {
    params.set('loop', '1');
    params.set('playlist', id);
  }
  if (typeof window !== 'undefined') params.set('origin', window.location.origin);
  return `https://www.youtube.com/embed/${id}?${params.toString()}`;
};

const thumbUrl = (id) => `https://img.youtube.com/vi/${id}/hqdefault.jpg`;

const mod = (n, m) => ((n % m) + m) % m;

export const VideoTestimonials = () => {
  const [start, setStart] = useState(0); // index of left-most visible tile
  const [openId, setOpenId] = useState(null);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => setStart((s) => mod(s + 1, VIDEOS.length)), []);
  const prev = useCallback(() => setStart((s) => mod(s - 1, VIDEOS.length)), []);

  useEffect(() => {
    if (paused || openId) return;
    const t = setInterval(next, ROTATION_MS);
    return () => clearInterval(t);
  }, [paused, openId, next]);

  // Compute the 3 visible indices for desktop
  const visibleIndices = Array.from({ length: VISIBLE }, (_, i) => mod(start + i, VIDEOS.length));

  return (
    <section
      className="py-28 md:py-36 bg-[#1F2A4A] text-[#E8DCC8] relative overflow-hidden"
      data-testid="video-testimonials-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* subtle texture */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
           style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #C9B896 1px, transparent 1px)', backgroundSize: '32px 32px' }} />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.4em] text-[#C9B896] mb-4">ILS TÉMOIGNENT</p>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white font-light leading-[0.95] tracking-tight">
              Témoignages vidéo <span className="italic text-[#C9B896]">des élèves.</span>
            </h2>
          </div>
          <p className="text-white/65 max-w-md text-base md:text-lg leading-relaxed">
            Ce que ça change vraiment d'apprendre le métier à Istres — raconté par ceux qui en sont sortis.
          </p>
        </div>

        {/* CAROUSEL — 3 tiles visible, scrolls 1-by-1 */}
        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {visibleIndices.map((idx, slot) => {
              const v = VIDEOS[idx];
              return (
                <button
                  key={`${idx}-${slot}`}
                  onClick={() => setOpenId(v.id)}
                  data-testid={`video-tile-${slot}`}
                  className="group relative aspect-video rounded-2xl overflow-hidden bg-[#0C1325] shadow-2xl shadow-black/40 ring-1 ring-[#C9B896]/15 hover:ring-[#C9B896]/50 transition-all duration-500 animate-fade-in"
                >
                  {/* Live autoplaying muted preview */}
                  <iframe
                    title={`Témoignage ${idx + 1}`}
                    src={embedUrl(v.id, { muted: true, autoplay: true })}
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    frameBorder="0"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    loading="lazy"
                  />
                  {/* Soft bottom gradient for caption legibility */}
                  <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0C1325]/90 via-[#0C1325]/30 to-transparent pointer-events-none" />
                  {/* Hover overlay with play button */}
                  <div className="absolute inset-0 bg-[#0C1325]/0 group-hover:bg-[#0C1325]/35 transition-colors duration-500 flex items-center justify-center pointer-events-none">
                    <span className="opacity-0 group-hover:opacity-100 w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#C9B896] text-[#1F2A4A] flex items-center justify-center shadow-2xl shadow-black/40 transition-all duration-500 group-hover:scale-110">
                      <Play className="w-5 h-5 md:w-6 md:h-6 ml-0.5" fill="currentColor" />
                    </span>
                  </div>
                  {/* Caption */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 text-left pointer-events-none">
                    <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-1">Témoignage {idx + 1} / {VIDEOS.length}</p>
                    <p className="text-white/95 text-sm md:text-base font-medium leading-snug">{v.author}</p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Arrows */}
          <button
            onClick={prev}
            aria-label="Témoignage précédent"
            data-testid="video-prev"
            className="absolute left-2 md:-left-5 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-[#C9B896] hover:text-[#1F2A4A] backdrop-blur-md text-white flex items-center justify-center transition-colors duration-300 z-10"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={next}
            aria-label="Témoignage suivant"
            data-testid="video-next"
            className="absolute right-2 md:-right-5 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-[#C9B896] hover:text-[#1F2A4A] backdrop-blur-md text-white flex items-center justify-center transition-colors duration-300 z-10"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Dots — one per video, position highlight on `start` */}
        <div className="flex items-center justify-center gap-2 mt-10">
          {VIDEOS.map((_, i) => (
            <button
              key={i}
              onClick={() => setStart(i)}
              aria-label={`Aller au témoignage ${i + 1}`}
              data-testid={`video-dot-${i}`}
              className={`h-px transition-all duration-500 ${
                i === start ? 'w-12 bg-[#C9B896]' : 'w-6 bg-white/30 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Full-sound modal */}
      <Dialog open={!!openId} onOpenChange={(v) => { if (!v) setOpenId(null); }}>
        <DialogContent
          className="max-w-5xl w-[95vw] p-0 bg-[#0C1325] border-[#C9B896]/20 [&>button]:hidden"
          data-testid="video-modal"
        >
          <button
            onClick={() => setOpenId(null)}
            aria-label="Fermer"
            className="absolute -top-3 -right-3 z-20 w-9 h-9 rounded-full bg-[#C9B896] text-[#1F2A4A] flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
            data-testid="video-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="aspect-video">
            {openId && (
              <iframe
                title="Témoignage en lecture"
                src={embedUrl(openId, { muted: false, autoplay: true, controls: true, loop: false })}
                className="w-full h-full"
                frameBorder="0"
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
              />
            )}
          </div>
          {openId && (
            <a
              href={`https://www.youtube.com/watch?v=${openId}`}
              target="_blank"
              rel="noreferrer"
              className="block text-center text-[10px] font-mono uppercase tracking-[0.3em] text-[#C9B896]/70 hover:text-[#C9B896] py-3 transition-colors"
              data-testid="video-modal-yt-fallback"
            >
              Si la vidéo ne se charge pas — Voir sur YouTube →
            </a>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default VideoTestimonials;
